/**
 * Session Management Utility
 * Handles session timeout (15 minutes of inactivity) and token validation
 */

const SESSION_TIMEOUT = 15 * 60 * 1000; // 15 minutes in milliseconds
const LAST_ACTIVITY_KEY = 'lastActivity';
const SESSION_START_KEY = 'sessionStart';

/**
 * Check if JWT token is expired
 */
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    return exp < now;
  } catch (e) {
    console.warn('Could not decode token:', e);
    return true; // If we can't decode, consider it expired
  }
};

/**
 * Get token expiration time
 */
export const getTokenExpiration = (token) => {
  if (!token) return null;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000; // Convert to milliseconds
  } catch (e) {
    return null;
  }
};

/**
 * Update last activity timestamp
 */
export const updateLastActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
};

/**
 * Get last activity timestamp
 */
export const getLastActivity = () => {
  const lastActivity = localStorage.getItem(LAST_ACTIVITY_KEY);
  return lastActivity ? parseInt(lastActivity, 10) : null;
};

/**
 * Check if session has expired due to inactivity (15 minutes)
 */
export const isSessionExpired = () => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return true;
  
  const now = Date.now();
  const timeSinceLastActivity = now - lastActivity;
  
  return timeSinceLastActivity > SESSION_TIMEOUT;
};

/**
 * Initialize session (call on login)
 */
export const initSession = () => {
  updateLastActivity();
  localStorage.setItem(SESSION_START_KEY, Date.now().toString());
};

/**
 * Clear session (call on logout)
 */
export const clearAuthCookie = () => {
  if (typeof document === "undefined") return;

  const host = window.location.hostname;
  let domain = "";
  if (host === "test.commray.com" || host.endsWith(".test.commray.com")) {
    domain = "; domain=.test.commray.com";
  } else if (host === "commray.com" || host.endsWith(".commray.com")) {
    domain = "; domain=.commray.com";
  }

  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `commray_auth=; Max-Age=0; Path=/${domain}${secure}; SameSite=Lax`;
};

export const clearSession = () => {
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_START_KEY);
  localStorage.removeItem("token");
  localStorage.removeItem("username");
  localStorage.removeItem("userRole");
  clearAuthCookie();
};

/**
 * Get time remaining until session expires (in milliseconds)
 */
export const getTimeUntilSessionExpires = () => {
  const lastActivity = getLastActivity();
  if (!lastActivity) return 0;
  
  const now = Date.now();
  const timeSinceLastActivity = now - lastActivity;
  const timeRemaining = SESSION_TIMEOUT - timeSinceLastActivity;
  
  return Math.max(0, timeRemaining);
};

/**
 * Setup activity listeners to track user activity
 */
export const setupActivityListeners = (onSessionExpired) => {
  const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
  
  const updateActivity = () => {
    updateLastActivity();
  };
  
  // Add event listeners
  events.forEach(event => {
    document.addEventListener(event, updateActivity, { passive: true });
  });
  
  // Check session expiration periodically (every minute)
  const checkInterval = setInterval(() => {
    if (isSessionExpired()) {
      clearInterval(checkInterval);
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
      // Call callback to handle logout
      if (onSessionExpired) {
        onSessionExpired();
      }
    }
  }, 60 * 1000); // Check every minute
  
  // Return cleanup function
  return () => {
    clearInterval(checkInterval);
    events.forEach(event => {
      document.removeEventListener(event, updateActivity);
    });
  };
};









